import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { FormControl, FormLabel, Input, makeStyles } from '@material-ui/core'
import { setToolOptions } from 'store/tool/actions'
import { getToolOptions, getSelectedMaterials, getMaterials } from 'store/selectors'
import { updateShapeData } from 'store/materials/actions'

const useStyles = makeStyles({
  root: {
    padding: '15px',
  },
  formControl: {
    width: '100%',
    marginBottom: '15px',
  },
  label: {
    marginBottom: '5px',
  },
})

interface StateProps {
  options: {
    strokeWidth: number
  }
  selectedMaterials: (string | number)[]
  materials: { [key: string]: ReduxStore.Materials.Data.IMaterial }
}

interface DispatchProps {
  dispatchSetToolOptions: (options: any) => void
  dispatchUpdateShapeData: (newShape: ReduxStore.Materials.Data.IOverlay) => void
}

type Props = StateProps & DispatchProps

const LineToolOptions: React.FC<Props> = ({ 
  options, 
  dispatchSetToolOptions, 
  selectedMaterials,
  materials,
  dispatchUpdateShapeData 
}) => {
  const classes = useStyles()
  const reduxStrokeWidth = (options && options.strokeWidth) ? options.strokeWidth : 2
  
  // Local state to handle the input value, allowing empty string
  const [inputValue, setInputValue] = useState(reduxStrokeWidth.toString())

  // Update local state when Redux state changes
  useEffect(() => {
    setInputValue(reduxStrokeWidth.toString())
  }, [reduxStrokeWidth])

  const handleStrokeWidthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    
    // Always update the local state to allow empty input
    setInputValue(value)
    
    // Only update Redux if we have a valid number
    const numValue = Number(value)
    if (value !== '' && !isNaN(numValue) && numValue >= 1 && numValue <= 100) {
      // Update tool options for new lines
      dispatchSetToolOptions({
        strokeWidth: numValue,
      })
      
      // Update stroke width for selected lines
      selectedMaterials.forEach(materialId => {
        const material = materials[materialId]
        if (material && material.shape === 'line') {
          const shapeData = JSON.parse(material.shape_data)
          shapeData.strokeWidth = numValue
          dispatchUpdateShapeData({
            overlay_id: material.overlay_id,
            material_id: material.material_id,
            drawing_id: material.drawing_id,
            shape_data: JSON.stringify(shapeData),
            shape: material.shape
          })
        }
      })
    }
  }
  
  const handleBlur = () => {
    // On blur, if the input is empty or invalid, reset to the Redux value
    const numValue = Number(inputValue)
    if (inputValue === '' || isNaN(numValue) || numValue < 1 || numValue > 100) {
      setInputValue(reduxStrokeWidth.toString())
    }
  }

  // Update stroke width display when selection changes
  useEffect(() => {
    // If a single line is selected, show its stroke width
    if (selectedMaterials.length === 1) {
      const material = materials[selectedMaterials[0]]
      if (material && material.shape === 'line') {
        const shapeData = JSON.parse(material.shape_data)
        if (shapeData.strokeWidth && shapeData.strokeWidth !== reduxStrokeWidth) {
          dispatchSetToolOptions({
            strokeWidth: shapeData.strokeWidth,
          })
        }
      }
    }
  }, [selectedMaterials, materials, reduxStrokeWidth, dispatchSetToolOptions])

  return (
    <div className={classes.root}>
      <FormControl className={classes.formControl}>
        <FormLabel className={classes.label}>Line Thickness</FormLabel>
        <Input
          type="number"
          value={inputValue}
          onChange={handleStrokeWidthChange}
          onBlur={handleBlur}
          inputProps={{ min: 1, max: 100 }}
        />
      </FormControl>
    </div>
  )
}

const mapStateToProps = (state: ReduxStore.State) => {
  const options = getToolOptions(state, 'line') || {};
  return {
    options: { strokeWidth: (options as any).strokeWidth || 2 },
    selectedMaterials: getSelectedMaterials(state),
    materials: getMaterials(state)
  };
}

const mapDispatchToProps = {
  dispatchSetToolOptions: (options: any) => setToolOptions('line', options),
  dispatchUpdateShapeData: updateShapeData
}

export default connect(mapStateToProps, mapDispatchToProps)(LineToolOptions)